import React from 'react'
import { Typography } from '@mui/material'
import { withStyles } from '@mui/styles'
import { Checkbox, Box } from '@mui/material'

import { checkboxComponentStyles } from './checkbox.styles'

/**
 * @typedef {object} CheckboxChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop `name`.
 * @property {boolean} target.value - El nuevo estado booleano del checkbox.
 */

/**
 * @typedef {object} CheckboxComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {boolean} [readOnly=false] - Si es `true`, el checkbox se muestra pero está deshabilitado.
 * @property {boolean} [required=false] - Prop recibida pero no utilizada actualmente en la lógica del componente.
 * @property {boolean} [value=false] - El estado actual del checkbox (marcado o no marcado).
 * @property {boolean} [hidden] - Si es `true`, el componente no renderiza nada.
 * @property {string} title - El título o etiqueta principal que se muestra sobre el checkbox.
 * @property {string} label - El texto que se muestra junto al checkbox.
 * @property {string} name - El nombre del campo, que se incluirá en el objeto de evento sintético del `onChange`.
 * @property {function(CheckboxChangeEvent): void} onChange - La función callback que se ejecuta cuando el estado del checkbox cambia.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título.
 */

/**
 * Un componente que renderiza un único checkbox con un título y una etiqueta asociada.
 *
 * Funciona como un componente controlado, gestionando su estado a través de las props `value` y `onChange`.
 * Proporciona un modo de solo lectura (`readOnly`) para deshabilitar la interacción y una prop `hidden`
 * para ocultar el componente por completo de la UI.
 *
 * @component
 * @example
 * const MySettings = () => {
 * const [notifications, setNotifications] = useState(true);
 *
 * const handleChange = (e) => {
 * console.log(`El campo '${e.target.name}' ahora es: ${e.target.value}`);
 * setNotifications(e.target.value);
 * };
 *
 * return (
 * <CheckboxComponent
 * title="Configuración de Notificaciones"
 * label="Recibir notificaciones por correo"
 * name="emailNotifications"
 * value={notifications}
 * onChange={handleChange}
 * />
 * );
 * };
 *
 * @param {CheckboxComponentProps} props - Las props del componente CheckboxComponent.
 */
export const CheckboxComponent = withStyles(checkboxComponentStyles)(({
  classes,
  readOnly = false,
  required = false,
  value = false,
  hidden,
  ...props
}) => {
  const typographyVariant = props.fontSize === 'small' ? '2' : '1'

  const handleChange = (e) => {
    let newValue
    if (e.target.checked) {
      newValue = e.target.checked
    } else if (!e.target.checked) {
      newValue = e.target.checked
    }
    props.onChange({ target: { name: props.name, value: newValue } })
  }

  return (
    <>
      {hidden ? null : (
        <>
          <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
            {props.title}
          </Typography>
          {readOnly ? (
            <Box display={'flex'} alignItems={'center'}>
              <Checkbox
                className={classes.checkbox}
                color="primary"
                size="small"
                checked={value}
                onChange={handleChange}
                name={props.name}
                disabled
              />
              <Typography variant="body1" color="textSecondary">
                {props.label}
              </Typography>
            </Box>
          ) : (
            <Box display={'flex'} alignItems={'center'}>
              <Checkbox
                className={classes.checkbox}
                color="primary"
                size="small"
                checked={value}
                onChange={handleChange}
                name={props.name}
              />
              <Typography variant="body1" color="textSecondary">
                {props.label}
              </Typography>
            </Box>
          )}
        </>
      )}
    </>
  )
})
